import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SchoolNoticeRichEditor from '../components/schoolNotice/SchoolNoticeRichEditor';
import { PLATFORM_ADMIN_EMAIL } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  createSchoolLegalDocumentAsCurrent,
  deleteSchoolLegalDocument,
  listSchoolLegalDocuments,
  updateSchoolLegalDocument,
} from '../services/schoolLegalDocumentService';
import type { SchoolLegalDocumentRow, SchoolLegalKind } from '../types/schoolLegalDocument';
import { loadSchoolLegalDocumentsCache } from '../utils/platformGlobalAdminCache';

type LegalCopy = {
  tab: string;
  listHead: string;
  registered: string;
  previous: string;
  createCta: string;
  modifiedCurrent: string;
  modifiedPrev: string;
  editorNew: string;
  editorEdit: string;
  placeholder: string;
};

function isAdminSessionEmail(email: string): boolean {
  return email.trim().toLowerCase() === PLATFORM_ADMIN_EMAIL;
}

function formatLegalDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'long', timeStyle: 'short' });
}

function legalBodyLooksEmpty(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').replace(/\s|&nbsp;/gi, '').trim();
}

const KIND_COPY: Record<SchoolLegalKind, LegalCopy> = {
  terms: {
    tab: '서비스 이용약관',
    listHead: '서비스 이용약관 버전',
    registered: '등록된 서비스 이용약관',
    previous: '이전 서비스 이용약관',
    createCta: '서비스 이용약관 만들기',
    modifiedCurrent: '최종 수정',
    modifiedPrev: '수정일',
    editorNew: '서비스 이용약관 작성',
    editorEdit: '서비스 이용약관 수정',
    placeholder: '서비스 이용약관 내용을 입력하세요...',
  },
  privacy: {
    tab: '개인정보처리방침',
    listHead: '개인정보처리방침 버전',
    registered: '등록된 개인정보처리방침',
    previous: '이전 개인정보처리방침',
    createCta: '개인정보처리방침 만들기',
    modifiedCurrent: '최종 수정',
    modifiedPrev: '수정일',
    editorNew: '개인정보처리방침 작성',
    editorEdit: '개인정보처리방침 수정',
    placeholder: '개인정보처리방침 내용을 입력하세요...',
  },
};

const AdminLegalDocumentsPage: React.FC = () => {
  const [sessionEmail, setSessionEmail] = useState('');
  const [kind, setKind] = useState<SchoolLegalKind>('terms');
  const [list, setList] = useState<SchoolLegalDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isAdmin = useMemo(() => isAdminSessionEmail(sessionEmail), [sessionEmail]);
  const copy = KIND_COPY[kind];

  useEffect(() => {
    let c = false;
    void getSession()
      .then((s) => {
        if (!c) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (!c) setSessionEmail('');
      });
    return () => {
      c = true;
    };
  }, []);

  const selectRow = useCallback((row: SchoolLegalDocumentRow) => {
    setSelectedId(row.id);
    setDraftBody(row.body_html);
    setSaveError(null);
  }, []);

  const selectAppliedOrClear = useCallback((rows: SchoolLegalDocumentRow[]) => {
    const current = rows.find((r) => r.is_current) ?? null;
    if (current) {
      setSelectedId(current.id);
      setDraftBody(current.body_html);
    } else {
      setSelectedId(null);
      setDraftBody('');
    }
    setSaveError(null);
  }, []);

  const loadList = useCallback(async () => {
    const cached = loadSchoolLegalDocumentsCache(kind);
    const hasCache = cached.found;
    if (hasCache) {
      // 캐시를 즉시 표시하되, listSchoolLegalDocuments의 head(id/updated_at) 비교로 백그라운드 재검증한다.
      setList(cached.rows);
      selectAppliedOrClear(cached.rows);
      setLoading(false);
      setLoadError(null);
    } else {
      setLoading(true);
      setLoadError(null);
    }
    try {
      const rows = await listSchoolLegalDocuments(kind);
      setList(rows);
      selectAppliedOrClear(rows);
    } catch (e) {
      if (!hasCache) {
        setLoadError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
        setList([]);
        selectAppliedOrClear([]);
      }
    } finally {
      setLoading(false);
    }
  }, [kind, selectAppliedOrClear]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadList();
  }, [isAdmin, loadList]);

  const startNew = useCallback(() => {
    setSelectedId(null);
    setDraftBody('');
    setSaveError(null);
  }, []);

  const cancelDraft = useCallback(() => {
    setSaveError(null);
    if (selectedId) {
      const row = list.find((r) => r.id === selectedId);
      if (row) selectRow(row);
      else selectAppliedOrClear(list);
    } else {
      selectAppliedOrClear(list);
    }
  }, [list, selectedId, selectRow, selectAppliedOrClear]);

  const handleSave = async () => {
    if (!isAdmin) return;
    if (legalBodyLooksEmpty(draftBody)) {
      setSaveError('내용을 입력해 주세요.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (selectedId) {
        const updated = await updateSchoolLegalDocument(selectedId, draftBody);
        setList((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelectedId(updated.id);
      } else {
        const created = await createSchoolLegalDocumentAsCurrent(kind, draftBody);
        const rows = await listSchoolLegalDocuments(kind);
        setList(rows);
        setSelectedId(created.id);
        setDraftBody(created.body_html);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async (row: SchoolLegalDocumentRow) => {
    if (!isAdmin || row.is_current) return;
    if (!window.confirm('이 버전을 삭제할까요?')) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteSchoolLegalDocument(row.id);
      const next = list.filter((r) => r.id !== row.id);
      setList(next);
      if (selectedId === row.id) selectAppliedOrClear(next);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const switchKind = (next: SchoolLegalKind) => {
    if (next === kind || saving) return;
    setKind(next);
    setSaveError(null);
  };

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-admin-legal-title">
        <h2 id="cp-admin-legal-title" className="cp-page-title">
          약관 및 개인정보처리방침 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-legal-page--edu"
      aria-labelledby="cp-admin-legal-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-legal-title">약관 및 개인정보처리방침 관리</h2>
          <p className="cp-admin-legal-subtitle">
            플랫폼 전역 문서입니다. 비로그인 회원가입·모든 사용자가 <strong>현행</strong> 버전을 조회할 수 있으며, 편집은
            플랫폼 관리자만 가능합니다.
          </p>
        </div>
      </div>

      <div className="cp-admin-legal-tabs" role="tablist" aria-label="문서 종류">
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'terms'}
          className={'cp-admin-legal-tab' + (kind === 'terms' ? ' is-active' : '')}
          onClick={() => switchKind('terms')}
          disabled={saving}
        >
          {KIND_COPY.terms.tab}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'privacy'}
          className={'cp-admin-legal-tab' + (kind === 'privacy' ? ' is-active' : '')}
          onClick={() => switchKind('privacy')}
          disabled={saving}
        >
          {KIND_COPY.privacy.tab}
        </button>
      </div>

      <div className="cp-admin-notices-grid" role="presentation">
        <article className="cp-dashboard-block cp-admin-notices-list-block" aria-labelledby="cp-admin-legal-list-head">
          <h3 className="cp-dashboard-block__head" id="cp-admin-legal-list-head">
            <span className="cp-dashboard-block__head-title">{copy.listHead}</span>
          </h3>
          <div className="cp-dashboard-block__body cp-admin-notices-list-body-edu">
            <button
              type="button"
              className="cp-admin-notices-create-cta cp-admin-legal-create-cta"
              onClick={startNew}
              disabled={saving}
            >
              + {copy.createCta}
            </button>
            {loading ? (
              <p className="cp-dashboard-placeholder" role="status">
                불러오는 중…
              </p>
            ) : loadError ? (
              <p className="cp-dashboard-placeholder" role="alert">
                {loadError}
              </p>
            ) : list.length === 0 ? (
              <p className="cp-dashboard-placeholder">등록된 버전이 없습니다. 위 버튼으로 새로 만들 수 있습니다.</p>
            ) : (
              <ul className="cp-admin-notices-edu-list">
                {list.map((row) => (
                  <li key={row.id} className="cp-admin-notices-edu-card" role="none">
                    <div className={'cp-admin-notices-edu-card-inner' + (selectedId === row.id ? ' is-active' : '')}>
                      <button
                        type="button"
                        className="cp-admin-notices-edu-card-main cp-admin-legal-version-card-main"
                        onClick={() => selectRow(row)}
                        aria-current={selectedId === row.id ? 'true' : undefined}
                      >
                        <span className="cp-admin-legal-version-role">
                          {row.is_current ? copy.registered : copy.previous}
                        </span>
                        <span className="cp-admin-notices-edu-card-date">
                          {row.is_current ? copy.modifiedCurrent : copy.modifiedPrev}:{' '}
                          {formatLegalDateTime(row.updated_at)}
                        </span>
                      </button>
                      <div className="cp-admin-notices-edu-card-tools">
                        {row.is_current ? (
                          <span className="cp-admin-list-applied-badge" title="사용자·회원가입 화면에 노출되는 현행 문서">
                            적용 중
                          </span>
                        ) : null}
                        {!row.is_current ? (
                          <button
                            type="button"
                            className="cp-admin-notices-icon-btn"
                            title="삭제"
                            aria-label="삭제"
                            disabled={saving}
                            onClick={() => void handleDeleteRow(row)}
                          >
                            <img
                              src="/Trash.svg"
                              alt=""
                              className="cp-admin-notices-tool-icon"
                              width={18}
                              height={18}
                              decoding="async"
                            />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </article>

        <article className="cp-dashboard-block cp-admin-notices-editor-block" aria-labelledby="cp-admin-legal-editor-head">
          <h3 className="cp-dashboard-block__head" id="cp-admin-legal-editor-head">
            <span className="cp-dashboard-block__head-title">{selectedId ? copy.editorEdit : copy.editorNew}</span>
          </h3>
          <div className="cp-dashboard-block__body cp-admin-notices-editor-body cp-admin-notices-editor-body--edu">
            <div className="cp-admin-notices-field cp-admin-notices-field--notice-editor">
              <SchoolNoticeRichEditor
                key={`${kind}-${selectedId ?? 'new'}`}
                value={draftBody}
                onChange={setDraftBody}
                disabled={saving}
                ariaLabel={copy.tab + ' 본문'}
                placeholder={copy.placeholder}
              />
            </div>
            {saveError ? (
              <p className="cp-admin-notices-save-error" role="alert">
                {saveError}
              </p>
            ) : null}
            <div className="cp-admin-notices-actions cp-admin-notices-actions--edu">
              <button type="button" className="cp-admin-notices-btn-cancel" onClick={cancelDraft} disabled={saving}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary cp-admin-notices-btn-save"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default AdminLegalDocumentsPage;
